import React, { useEffect, useState } from 'react';
import { Calendar } from 'lucide-react';
import { showcaseAPI } from '@services/showcaseApi';
import Layout from '@components/layout/Layout';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import ResponsiveImage from '@components/common/ResponsiveImage';
import type { ShowcaseCard } from '@/types/showcase';

const EventsPage: React.FC = () => {
  const [albums, setAlbums] = useState<ShowcaseCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await showcaseAPI.list();
      setAlbums(res.data ?? []);
    } catch {
      setError('Unable to load the showcase.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (error) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Unable to Load Albums</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={load}>Try Again</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Featured Albums</h1>
          <p className="text-xl text-gray-600">
            A curated showcase of albums from photographers on Pixel Share.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="text-center">
              <LoadingSpinner size="large" />
              <p className="text-gray-500 mt-4">Loading albums...</p>
            </div>
          </div>
        ) : albums.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No featured albums yet</h3>
            <p className="text-gray-500">
              Check back soon — new albums are featured as photographers share their work.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {albums.map(album => (
              <AlbumCard key={album.eventId} album={album} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

// Links OUT to the photographer's microsite gallery (no in-app gallery).
const AlbumCard: React.FC<{ album: ShowcaseCard }> = ({ album }) => (
  <a
    href={album.micrositeUrl}
    target="_blank"
    rel="noopener noreferrer"
    className="group bg-white rounded-lg shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden"
  >
    <div className="aspect-video relative">
      <ResponsiveImage
        src={album.coverImage || '/placeholder-event.jpg'}
        alt={album.title}
        className="w-full h-full"
      />
      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300" />
    </div>
    <div className="p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-1 group-hover:text-primary-600 transition-colors">
        {album.title}
      </h3>
      <p className="text-sm text-gray-500">by {album.photographerName}</p>
    </div>
  </a>
);

export default EventsPage;
